'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useTransition,
} from 'react'

import type { CertificationType } from '@/types/recipient-search'

interface RecipientFilterContextValue {
  filterByNaics: (code: string) => void
  filterByCertification: (type: CertificationType) => void
  filterByState: (state: string) => void
  showSubsidiaries: (hash: string, name: string) => void
  selectedNaicsCodes: ReadonlySet<string>
  isPending: boolean
}

const RecipientFilterContext =
  createContext<RecipientFilterContextValue | null>(null)

interface RecipientFilterProviderProps {
  children: React.ReactNode
  onFilterByNaics: (code: string) => void
  onFilterByCertification: (type: CertificationType) => void
  onFilterByState: (state: string) => void
  onShowSubsidiaries: (hash: string, name: string) => void
  selectedNaicsCodes: ReadonlySet<string>
}

/**
 * Provider for filter actions from recipient cards.
 * Uses useTransition to mark filter updates as non-urgent,
 * keeping the UI responsive during search updates.
 */
export const RecipientFilterProvider: React.FC<
  RecipientFilterProviderProps
> = ({
  children,
  onFilterByNaics,
  onFilterByCertification,
  onFilterByState,
  onShowSubsidiaries,
  selectedNaicsCodes,
}) => {
  const [isPending, startTransition] = useTransition()

  // Wrap filter actions in startTransition to mark them as non-urgent
  // This allows React to keep showing current results while fetching new ones
  const filterByNaics = useCallback(
    (code: string) => {
      startTransition(() => {
        onFilterByNaics(code)
      })
    },
    [onFilterByNaics],
  )

  const filterByCertification = useCallback(
    (type: CertificationType) => {
      startTransition(() => {
        onFilterByCertification(type)
      })
    },
    [onFilterByCertification],
  )

  const filterByState = useCallback(
    (state: string) => {
      startTransition(() => {
        onFilterByState(state)
      })
    },
    [onFilterByState],
  )

  const showSubsidiaries = useCallback(
    (hash: string, name: string) => {
      startTransition(() => {
        onShowSubsidiaries(hash, name)
      })
    },
    [onShowSubsidiaries],
  )

  const value = useMemo(
    () => ({
      filterByNaics,
      filterByCertification,
      filterByState,
      showSubsidiaries,
      selectedNaicsCodes,
      isPending,
    }),
    [
      filterByNaics,
      filterByCertification,
      filterByState,
      showSubsidiaries,
      selectedNaicsCodes,
      isPending,
    ],
  )

  return (
    <RecipientFilterContext.Provider value={value}>
      {children}
    </RecipientFilterContext.Provider>
  )
}

export function useRecipientFilterActions(): RecipientFilterContextValue | null {
  return useContext(RecipientFilterContext)
}
